import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import ArticleRecommendationCard from './ArticleRecommendationCard';
import { GetArticleRecommendationsResponse } from '../../ai-recommendation/types/GetArticleRecommendationsResponse';
import { getArticleRecommendations } from '../../ai-recommendation/services/ArticleRecommendationService';
import { ArticleTag } from '../../ai-recommendation/types/ArticleTag';
import { ArticleTagTerm } from 'ai-recommendation/types/ArticleTagTerm';
import { NativeAd } from 'ai-recommendation/types/ NativeAd';
import { render, createPortal } from 'react-dom';
import Button from '../../components/buttons/SimpleButton/Button';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

interface ArticleRecommendationContainerProps {}

const ArticleRecommendationContainer: FunctionComponent<ArticleRecommendationContainerProps> = () => {
  const [articles, setArticles] = useState<GetArticleRecommendationsResponse[]>([]);
  const [sectionTitle, setSectionTitle] = useState<string>('');
  const [readMoreLabel, setReadMoreLabel] = useState<string>('');
  const [recommendationLoading, setRecommendationLoading] = useState<boolean>(false);

  const userId = window.crm_user?.user.id;

  const renderNativeAd = (cardProps: NativeAd, container: Element, size: string) => {
    const cardElement = (
      <div>
        <ArticleRecommendationCard
          id={'index'}
          key={'index'}
          title={cardProps.title}
          tagLabel={'Native Ad'}
          authorName={cardProps.description}
          publishDate={''}
          imageLink={cardProps.image}
          readTime={'3 min'}
          cardSize={size}
          link={cardProps.url}
          isAdslot={false}
          authorLink={cardProps.url}
          categoryLink={cardProps.url}
        />
        <img
          src={cardProps.tracker}
          height="1"
          width="1"
          alt=""
          style={{ position: 'absolute', visibility: 'hidden', zIndex: -99999 }}
        />
      </div>
    );

    render(createPortal(cardElement, container), document.createElement('div'));
    (container as HTMLElement).style.display = 'block';
  };

  const removeLastItem = (containerClass: string) => {
    const container = document.querySelector(`.${containerClass}`)!;
    const lastItem = container.lastChild!;
    container.removeChild(lastItem);
  };

  const checkIfAdSlot = (expected: number, index: number): boolean => {
    // @ts-ignore
    const { country } = window?.Article;

    return expected === index && country.toUpperCase() === 'CZ';
  };

  const receiveNativeAd = useCallback((event: any) => {
    let eventData = event.data;
    let dataType = typeof eventData;

    if (dataType === 'object') {
      eventData = JSON.stringify(eventData);
      dataType = typeof eventData;
    }

    if (dataType === 'string') {
      if (eventData.includes('_FRBSNTV_') && eventData.includes('https://tpc.googlesyndication.com/')) {
        const allElements = eventData.split('_FRBSNTV_');

        if (allElements.length === 5) {
          let nativeInvalid = false;
          if (!allElements[1].startsWith('https://tpc.googlesyndication.com/')) {
            nativeInvalid = true;
          }
          if (!allElements[2].startsWith('https://adclick.g.doubleclick.net/')) {
            nativeInvalid = true;
          }

          if (allElements[4] !== '') {
            allElements[4] = allElements[4].replace('[timestamp]', new Date().getTime());
          }
          if (!nativeInvalid) {
            let nativeAdProps: NativeAd = {
              title: allElements[0],
              image: allElements[1],
              url: allElements[2],
              description: allElements[3],
              tracker: allElements[4],
            };

            // Set description link if it's a URL
            const pattern = /^(?:https?:\/\/)?(?:(?:w{3,4}\.)?([^:/\n]+))/im;
            const matches = nativeAdProps.description.match(pattern);
            if (matches && matches.length >= 2) {
              nativeAdProps.description = matches[1];
            }

            const nativeContainer = document.querySelector('#native-ad-container')!;
            const secondaryNativeContainer = document.querySelector('#native-ad-container-secondary')!;

            if (nativeContainer.childNodes.length === 1 && !(nativeContainer.childNodes[0].childNodes.length === 0)) {
              renderNativeAd(nativeAdProps, nativeContainer, 'small');
              removeLastItem('recommendations__featured-posts-wrapper');
            } else if (
              secondaryNativeContainer.childNodes.length === 1 &&
              !(secondaryNativeContainer.childNodes[0].childNodes.length === 0)
            ) {
              renderNativeAd(nativeAdProps, secondaryNativeContainer, 'medium');
              removeLastItem('recommendations__posts-wrapper');
            } else {
              return;
            }
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    // @ts-ignore
    const article = window?.Article;
    const country = article?.country.toUpperCase() as 'HU' | 'SK' | 'CZ';

    setExcludeArticleIdsToLocalStorage(article.id);
    const localStorageExcludeIds = localStorage.getItem('aiVisitedIds');

    setSectionTitleByCountry(country);
    setReadMoreLabelByCountry(country);

    fetchRecommendations(
      article?.id,
      article?.country.toUpperCase(),
      article?.country.toUpperCase() === 'HU' ? formatHuTags(article?.huTags) : article?.tags,
      article?.categoryId,
      localStorageExcludeIds ? JSON.parse(localStorageExcludeIds) : []
    );

    window.addEventListener('message', receiveNativeAd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiveNativeAd]);

  const setReadMoreLabelByCountry = (country: 'HU' | 'SK' | 'CZ') => {
    switch (country) {
      case 'HU':
        setReadMoreLabel('További cikkek');
        break;
      case 'SK':
        setReadMoreLabel('Ďalšie články');
        break;
      case 'CZ':
        setReadMoreLabel('Další články');
        break;
    }
  };

  const formatHuTags = (tags: ArticleTagTerm[]) => {
    return tags.map((tag) => ({
      id: tag.term_id,
      name: tag.name,
      slug: tag.slug,
    }));
  };

  const setExcludeArticleIdsToLocalStorage = (excludeId: string) => {
    const localStorageExcludeIds = localStorage.getItem('aiVisitedIds');
    let originalExcludeIds: string[] = [];

    if (localStorageExcludeIds) {
      originalExcludeIds = JSON.parse(localStorageExcludeIds);
    }

    if (!originalExcludeIds.includes(excludeId)) {
      localStorage.setItem('aiVisitedIds', JSON.stringify([excludeId, ...originalExcludeIds]));
    }
  };

  const fetchRecommendations = async (
    postId: number,
    country: 'HU' | 'SK' | 'CZ',
    articleTags: ArticleTag[],
    categoryId: number,
    excludeArticleIds?: number[]
  ) => {
    const fingerprintAgent = await FingerprintJS.load();
    const fingerprintResult = await fingerprintAgent.get();

    const response = await getArticleRecommendations(
      postId,
      country.toUpperCase() as 'HU' | 'SK' | 'CZ',
      articleTags,
      excludeArticleIds?.map(String) ?? [],
      categoryId,
      fingerprintResult.visitorId ?? '',
      userId?.toString(),
      window?.Article?.isLife
    );

    setArticles(response);

    setTimeout(() => {
      if (country.toUpperCase() === 'CZ') {
        // @ts-ignore
        window.getNativeAds();
      }
    });
  };

  const calculateCardSize = (index: number): 'large' | 'small' => {
    if (index === 0) {
      return 'large';
    } else if ([1, 2, 3, 4].includes(index)) {
      return 'small';
    }

    throw new Error('Invalid index, should be between 0 and 3');
  };

  const onReadMoreClick = async () => {
    setRecommendationLoading(true);

    let excludeArticleIds = articles?.map((article) => article.postId);
    const localStorageExcludeIds = localStorage.getItem('aiVisitedIds');

    if (localStorageExcludeIds) {
      excludeArticleIds = [...excludeArticleIds, ...JSON.parse(localStorageExcludeIds)];
    }

    if (!excludeArticleIds) {
      return;
    }

    // @ts-ignore
    const article = window?.Article;

    const fingerprintAgent = await FingerprintJS.load();
    const fingerprintResult = await fingerprintAgent.get();

    const newArticles = await getArticleRecommendations(
      article?.id,
      article?.country,
      article?.country.toUpperCase() === 'HU' ? formatHuTags(article?.huTags) : article?.tags,
      excludeArticleIds.map(String),
      article?.categoryId,
      fingerprintResult.visitorId ?? '',
      userId?.toString(),
      window?.Article?.isLife
    );

    // Limit the new articles to 9
    newArticles.splice(9);

    // In data if the article.postId occurs twice I should get an alert with all the articles that are duplicated
    const duplicateIds: number[] = [];
    const uniqueIds: number[] = [];

    articles.forEach((article) => {
      if (uniqueIds.includes(Number(article.postId))) {
        duplicateIds.push(Number(article.postId));
      } else {
        uniqueIds.push(Number(article.postId));
      }
    });

    if (duplicateIds.length > 0) {
      console.log(`There are ${duplicateIds.length} duplicates in the results: ${duplicateIds.join(', ')}`);
    }

    // setArticles([...articles, ...newArticles]);
    setArticles((prevArticles) => [...prevArticles, ...newArticles]);

    setRecommendationLoading(false);
  };

  const setSectionTitleByCountry = (country: 'HU' | 'SK' | 'CZ') => {
    switch (country) {
      case 'HU':
        setSectionTitle('Témához kapcsolódó cikkek');
        break;
      case 'SK':
        setSectionTitle('Čítajte viac');
        break;
      case 'CZ':
        setSectionTitle('K tématu');
        break;
    }
  };

  const featuredRecommendations = articles?.slice(0, 5);
  const remainingRecommendations = articles?.slice(5);

  return (
    <div>
      <h2 className="heading heading--archivo heading--800">{sectionTitle}</h2>

      <div className="recommendations__featured-posts-wrapper">
        {featuredRecommendations.map((recommendation, index) => (
          <ArticleRecommendationCard
            id={recommendation.postId}
            key={index}
            title={recommendation.title}
            tagLabel={recommendation.categoryName}
            authorName={recommendation.authorName}
            publishDate={recommendation.formattedPublishDate}
            imageLink={recommendation.featuredmedia}
            readTime=""
            cardSize={calculateCardSize(index)}
            link={recommendation.link}
            isAdslot={checkIfAdSlot(1, index)}
            authorLink={recommendation.authorLink}
            categoryLink={recommendation.categoryLink}
            position={index + 1}
          />
        ))}
      </div>

      {remainingRecommendations.length > 0 && (
        <div className="recommendations__posts-wrapper">
          {remainingRecommendations.map((recommendation, index) => (
            <ArticleRecommendationCard
              id={recommendation.postId}
              key={index}
              title={recommendation.title}
              tagLabel={recommendation.categoryName}
              authorName={recommendation.authorName}
              publishDate={recommendation.formattedPublishDate}
              imageLink={recommendation.featuredmedia}
              readTime=""
              cardSize="medium"
              link={recommendation.link}
              isAdslot={checkIfAdSlot(4, index)}
              authorLink={recommendation.authorLink}
              categoryLink={recommendation.categoryLink}
              position={index + 5}
            />
          ))}
        </div>
      )}

      <Button
        onClick={onReadMoreClick}
        isDisabled={recommendationLoading}
        className="recommendations__load-more-button"
        type="primary"
        size="medium"
        label={readMoreLabel}
      />
    </div>
  );
};

export default ArticleRecommendationContainer;
